// D2 — Web Documentation: direct-link / deep-link contract (TechSpec §29).
//
// The Web app is a state-driven SPA with NO URL router (D0 finding #4). The
// minimal canonical mechanism for deep/direct article navigation is a
// document-HASH deep link:
//
//   #/docs                     -> Documentation landing
//   #/docs/<canonical-slug>    -> a specific article, e.g. #/docs/user/chat/overview
//
// This is NOT a routing framework: it is a single hash namespace owned by the
// Documentation view, read on load and on `hashchange` (browser back/forward),
// and written with `history.pushState` for in-app internal-link navigation.
// A direct link therefore survives reload (the same article re-opens) and
// back/forward is supported — satisfying the "predictable direct link"
// requirement (D2-A7) without introducing a router.
//
// Anything not starting with "#/docs" is left untouched by the docs view
// (other app state owns the rest of the SPA).

const DOCS_PREFIX = "#/docs";

/** Build the direct-link hash for the landing (no article). */
export function docsLandingHash(): string {
  return DOCS_PREFIX;
}

/** Build the direct-link hash for a specific canonical article slug. */
export function docsArticleHash(slug: string): string {
  return `${DOCS_PREFIX}/${slug}`;
}

export type ParsedDocsLink = { view: "landing" } | { view: "article"; slug: string };

/**
 * Parse `location.hash` into a docs navigation target, or `null` when the
 * hash is not a docs deep link (so the docs view stays out of the way).
 *
 * Accepts both `#/docs` (landing) and `#/docs/<slug>` (article). The slug is
 * taken verbatim (it may contain `/`, e.g. `user/chat/overview`).
 */
export function parseDocsHash(hash: string): ParsedDocsLink | null {
  const h = hash || "";
  if (h === DOCS_PREFIX) return { view: "landing" };
  if (h.startsWith(DOCS_PREFIX + "/")) {
    const slug = h.slice(DOCS_PREFIX.length + 1).replace(/\/+$/, "");
    if (!slug) return { view: "landing" };
    return { view: "article", slug };
  }
  return null;
}

/**
 * Navigate to a docs deep link. Uses `history.pushState` so the URL updates
 * WITHOUT a full page reload and the SPA state stays in control (D2-A7 /
 * D2-A13 — no unnecessary external navigation). Back/forward then drive
 * `hashchange`, which the view listens to.
 */
export function setDocsHash(hash: string): void {
  if (typeof window === "undefined") return;
  if (window.location.hash === hash) return;
  try {
    window.history.pushState(null, "", hash);
  } catch {
    // Fallback for exotic environments (e.g. jsdom edge cases).
    window.location.hash = hash;
  }
}

/** Current docs deep link parsed from the live location (for initial load). */
export function currentDocsHash(): ParsedDocsLink | null {
  if (typeof window === "undefined") return null;
  return parseDocsHash(window.location.hash);
}
